import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  InputAdornment,
  FormHelperText
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Balance as BalanceIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import apiService from '../../../services/apiService';
import { useAuth } from '../../../context/AuthContext';
import { useSpecializedVoucherPermissions } from '../../../hooks/useSpecializedVoucherPermissions';

interface Account {
  _id: string;
  name: string;
  accountNumber: string;
}

interface FeeGroup {
  _id: string;
  name: string;
}

interface CompactTemplateData {
  id: string;
  name: string;
  debitAccounts: string[];
  creditAccounts: string[];
  feeGroups: string[];
  hasFees: boolean;
  allowMultipleDebitAccounts: boolean;
  allowMultipleCreditAccounts: boolean;
}

// تعريف واجهة لعنصر الرسوم
interface FeeItem {
  feeId: string;
  feeName: string;
  feeType: 'fixed' | 'percentage' | 'tax';
  amount: number;
}

// تعريف واجهة للرسوم المحسوبة
interface CalculatedFees {
  feeGroupId: string;
  feeGroupName: string;
  totalFeesAmount: number;
  totalFees: number; // إضافة حقل متوافق مع توقعات الخادم الخلفي
  total?: number;    // حقل بديل آخر قد يبحث عنه الخادم
  totalFixedFees?: number;
  totalPercentageFees?: number;
  totalTaxFees?: number;
  feeItems: FeeItem[];
}

interface CompactVoucherFormData {
  templateId: string;
  debitAccountId: string;
  creditAccountId: string;
  appliedFeeGroupId?: string;
  amount: number;
  operationNumber?: string;
  notes?: string;
}

const CompactBalanceVoucher: React.FC = () => {
  const { id: templateId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeBranch, user } = useAuth();
  const { hasVoucherTemplatePermission } = useSpecializedVoucherPermissions();
  
  // التحقق من كون المستخدم مدير نظام
  const isSystemAdmin = user?.isSystemAdmin || user?.role === 'admin';
  
  const [open, setOpen] = useState<boolean>(true);
  const [templateData, setTemplateData] = useState<CompactTemplateData | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [feeGroups, setFeeGroups] = useState<FeeGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  // إضافة حالة لتتبع حساب الرسوم
  const [calculatingFees, setCalculatingFees] = useState<boolean>(false);
  const [calculatedFees, setCalculatedFees] = useState<CalculatedFees | null>(null);
  
  // التحقق من وجود معرف قالب والصلاحيات
  useEffect(() => {
    if (!templateId) {
      setError('معرف قالب السند غير متوفر');
      return;
    }
    
    // التحقق من صلاحية الوصول لهذا القالب
    if (!hasVoucherTemplatePermission(templateId, 'view_page')) {
      setError('ليس لديك صلاحية الوصول إلى هذا السند المتخصص');
      return;
    }
    
    const fetchTemplateData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // استيراد apiWithContext مرة واحدة
        const { apiWithContext } = await import('../../../services/api');
        
        // جلب بيانات القالب باستخدام السياق المناسب
        const response = await apiWithContext.get(`/finance/specialized-vouchers/compact-template/${templateId}`, 'specialized-voucher').then(res => res.data);
        setTemplateData(response);
        
        // جلب الحسابات
        try {
          // استخدم المسار العادي مباشرة
          const accountsResponse = await apiService.get<Account[]>('/finance/accounts');
          setAccounts(accountsResponse);
        } catch (error) {
          // جرب مع السياق كحل بديل
          const accountsResponse = await apiWithContext.get('/finance/accounts', 'specialized-voucher').then(res => res.data);
          setAccounts(accountsResponse);
        }
        
        // جلب مجموعات الرسوم إذا كان القالب يدعم الرسوم
        if (response.hasFees) {
          const feeGroupsResponse = await apiWithContext.get('/finance/fee-groups', 'specialized-voucher').then(res => res.data);
          setFeeGroups(feeGroupsResponse);
        }
        
        setLoading(false);
      } catch (error: any) {
        console.error('خطأ في جلب بيانات القالب:', error);
        setError(error.response?.data?.message || 'حدث خطأ في جلب بيانات القالب');
        setLoading(false);
      }
    };
    
    fetchTemplateData();
  }, [templateId]);
  
  // مخطط التحقق من البيانات
  const validationSchema = Yup.object({
    debitAccountId: Yup.string().required('الحساب المدين مطلوب'),
    creditAccountId: Yup.string().required('الحساب الدائن مطلوب'),
    amount: Yup.number()
      .required('المبلغ مطلوب')
      .positive('يجب أن يكون المبلغ أكبر من صفر'),
    operationNumber: Yup.string().required('رقم العملية مطلوب'),
    appliedFeeGroupId: Yup.string().test(
      'required-if-fees-enabled',
      'مجموعة الرسوم مطلوبة',
      function(value) {
        // إذا كان القالب لا يدعم الرسوم أو لا يحتوي على مجموعات رسوم، فالحقل غير مطلوب
        if (!templateData?.hasFees || templateData?.feeGroups.length === 0) {
          return true;
        }
        
        // إذا كان القالب يدعم الرسوم، فمجموعة الرسوم مطلوبة
        return Boolean(value);
      }
    )
  });
  
  // الحصول على القيم الافتراضية للنموذج
  const getInitialValues = (): CompactVoucherFormData => {
    const initialValues: CompactVoucherFormData = {
      templateId: templateId || '',
      debitAccountId: '',
      creditAccountId: '',
      amount: 0,
      operationNumber: '',
      notes: ''
    };
    
    
    // تحديد الحساب المدين التلقائي إذا كان هناك حساب واحد فقط
    if (templateData && templateData.debitAccounts.length === 1) {
      initialValues.debitAccountId = templateData.debitAccounts[0];
    }
    
    // تحديد الحساب الدائن التلقائي إذا كان هناك حساب واحد فقط
    if (templateData && templateData.creditAccounts.length === 1) {
      initialValues.creditAccountId = templateData.creditAccounts[0];
    }
    
    // تحديد مجموعة الرسوم التلقائية إذا كان هناك مجموعة واحدة فقط
    if (templateData && templateData.hasFees && templateData.feeGroups.length === 1) {
      initialValues.appliedFeeGroupId = templateData.feeGroups[0];
    }
    
    return initialValues;
  };
  
  // دالة حساب الرسوم بشكل صريح
  const handleCalculateFees = async () => {
    // تأكد من إزالة أي رسائل خطأ سابقة
    setError(null);
    
    const { amount, appliedFeeGroupId } = formik.values;
    
    if (!templateData?.hasFees) {
      // القالب لا يدعم الرسوم
      return;
    }
    
    // تحقق صريح من جميع الشروط قبل المتابعة
    let hasError = false;
    
    if (!appliedFeeGroupId && amount <= 0) {
      setError('يجب تحديد مجموعة الرسوم والمبلغ لحساب الرسوم');
      hasError = true;
    } else if (!appliedFeeGroupId) {
      setError('يجب تحديد مجموعة الرسوم لحساب الرسوم');
      hasError = true;
    } else if (amount <= 0) {
      setError('يجب إدخال مبلغ صحيح أكبر من صفر لحساب الرسوم');
      hasError = true;
    }
    
    if (hasError) {
      setTimeout(() => setError(null), 5000);
      return;
    }
    
    setCalculatingFees(true);
    setError(null);
    
    try {
      // جلب تفاصيل مجموعة الرسوم
      const { apiWithContext } = await import('../../../services/api');
      const feeGroupDetails = await apiWithContext.get(`/finance/fee-groups/${appliedFeeGroupId}`, 'specialized-voucher').then(res => res.data);
      console.log('تفاصيل مجموعة الرسوم:', feeGroupDetails);
      
      if (feeGroupDetails && feeGroupDetails.fees) {
        // حساب الرسوم
        const amount = Number(formik.values.amount);
        let totalFixedFees = 0;
        let totalPercentageFees = 0;
        let totalTaxFees = 0;
        
        // التحقق من بيانات الرسوم
        console.log('عدد الرسوم المستلمة:', feeGroupDetails.fees.length);
        console.log('بيانات الرسوم الكاملة:', JSON.stringify(feeGroupDetails.fees));
        
        // تصنيف الرسوم حسب نوعها (مع مراعاة جميع أشكال البيانات المحتملة)
        const fixedFees = feeGroupDetails.fees.filter((fee: any) =>
          fee.type === 'fixed' || fee.feeType === 'fixed' ||
          (fee.percentage === false && !fee.description?.includes('ضريبة')));
        
        // تأكد من أن أي رسم قد يكون ضريبة لن يصنف كرسم نسبي
        const percentageFees = feeGroupDetails.fees.filter((fee: any) =>
          (fee.type === 'percentage' || fee.feeType === 'percentage' || fee.percentage === true) &&
          // استبعاد أي رسم قد يكون ضريبة
          !fee.description?.includes('ضريبة') &&
          !(fee.type === 'tax' || fee.feeType === 'tax') &&
          !(fee.amount === 15 || fee.value === 15));
        
        const taxFees = feeGroupDetails.fees.filter((fee: any) =>
          fee.type === 'tax' ||
          fee.feeType === 'tax' ||
          fee.description?.includes('ضريبة') ||
          // تصنيف الرسوم النسبية بقيمة 15% كضرائب (ضريبة القيمة المضافة)
          (fee.percentage === true && (fee.amount === 15 || fee.value === 15)));
        
        console.log('تصنيف الرسوم:', {
          ثابتة: fixedFees.length,
          نسبية: percentageFees.length,
          ضريبية: taxFees.length
        });
        
        // حساب الرسوم الثابتة
        fixedFees.forEach((fee: any) => {
          const feeAmount = Number(fee.amount || fee.value || 0);
          console.log(`رسم ثابت "${fee.name}": ${feeAmount}`);
          totalFixedFees += feeAmount;
        });
        
        // حساب الرسوم النسبية
        percentageFees.forEach((fee: any) => {
          const feeRate = Number(fee.amount || fee.value || 0);
          const calculatedAmount = amount * feeRate / 100;
          console.log(`رسم نسبي "${fee.name}": ${feeRate}% = ${calculatedAmount}`);
          // تأكد من استخدام القيمة المحسوبة وليس إعادة الحساب
          totalPercentageFees += calculatedAmount;
        });
        
        // حساب الضرائب (على مجموع الرسوم فقط)
        const feesSubtotal = totalFixedFees + totalPercentageFees;
        taxFees.forEach((fee: any) => {
          const taxRate = Number(fee.amount || fee.value || 0);
          const taxAmount = feesSubtotal * taxRate / 100;
          console.log(`ضريبة "${fee.name}": ${taxRate}% من ${feesSubtotal} = ${taxAmount}`);
          totalTaxFees += taxAmount;
        });
        
        const totalAllFees = totalFixedFees + totalPercentageFees + totalTaxFees;
        console.log('إجمالي حساب الرسوم:', {
          رسوم_ثابتة: totalFixedFees,
          رسوم_نسبية: `${totalPercentageFees} (محسوبة على أساس المبلغ الأصلي ${amount})`,
          ضرائب: `${totalTaxFees} (محسوبة على أساس مجموع الرسوم فقط ${feesSubtotal})`,
          المجموع: totalAllFees
        });
        
        // التأكد من أن معرف مجموعة الرسوم موجود (لتجنب خطأ TypeScript)
        if (!appliedFeeGroupId) {
          throw new Error('معرف مجموعة الرسوم غير موجود');
        }
        
        // إنشاء كائن الرسوم بتنسيق متوافق مع الخادم
        const fees: CalculatedFees = {
          feeGroupId: appliedFeeGroupId, // لن نصل إلى هنا إلا إذا كان معرف مجموعة الرسوم موجودًا
          feeGroupName: feeGroupDetails.name,
          totalFeesAmount: totalAllFees,
          totalFees: totalAllFees,
          total: totalAllFees,
          totalFixedFees: totalFixedFees,
          totalPercentageFees: totalPercentageFees,
          totalTaxFees: totalTaxFees,
          feeItems: [
            ...fixedFees.map((fee: any) => {
              // تسجيل تفاصيل كل رسم ثابت
              console.log(`إنشاء عنصر رسم ثابت: ${fee.name}, قيمة=${fee.amount}, نسبة=${fee.percentage}`);
              return {
                feeId: fee._id || `fixed-${Math.random()}`,
                feeName: fee.name,
                feeType: 'fixed',
                amount: Number(fee.amount || fee.value || 0)
              };
            }),
            ...percentageFees.map((fee: any) => {
              // استخدام نفس طريقة الحساب - على أساس المبلغ الأصلي فقط
              const feeRate = Number(fee.amount || fee.value || 0);
              const percentAmount = amount * feeRate / 100; // المبلغ الأصلي فقط
              
              // تسجيل معلومات مفصلة
              console.log(`إضافة رسم نسبي للمستند: ${fee.name}, معرف=${fee._id}, معدل=${feeRate}%, أساس=${amount}, قيمة=${percentAmount}`);
              
              return {
                feeId: fee._id || `percent-${Date.now()}-${Math.random()}`, // منع التكرار
                feeName: fee.name,
                feeType: 'percentage',
                amount: percentAmount
              };
            }),
            ...taxFees.map((fee: any) => {
              const taxRate = Number(fee.amount || fee.value || 0);
              const taxAmount = feesSubtotal * taxRate / 100;
              // تسجيل تفاصيل كل ضريبة بشكل واضح
              console.log(`إضافة ضريبة للمستند: ${fee.name}, معرف=${fee._id}, معدل=${taxRate}%, أساس=${feesSubtotal}, قيمة=${taxAmount}`);
              return {
                feeId: fee._id || `tax-${Date.now()}-${Math.random()}`, // منع التكرار
                feeName: fee.name,
                feeType: 'tax',
                amount: taxAmount
              };
            })
          ]
        };
        
        console.log('الرسوم المحسوبة:', fees);
        setCalculatedFees(fees);
      }
    } catch (error: any) {
      console.error('خطأ في حساب الرسوم:', error);
      setError(error.response?.data?.message || 'حدث خطأ في حساب الرسوم');
      setCalculatedFees(null);
    } finally {
      setCalculatingFees(false);
    }
  };
  
  // إعداد النموذج
  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!activeBranch || !activeBranch._id) {
        setError('لا يوجد فرع نشط. يرجى اختيار فرع أولاً.');
        return;
      }
      
      // التحقق من صلاحية إنشاء سند جديد
      if (!hasVoucherTemplatePermission(templateId!, 'create')) {
        setError('ليس لديك صلاحية إنشاء سند من هذا القالب');
        return;
      }
      
      // حماية قوية: منع حفظ السند برسوم صفر عند وجود مجموعة رسوم
      let feesToUse = calculatedFees;
      if (templateData?.hasFees && values.appliedFeeGroupId) {
        const totalFees = feesToUse?.totalFeesAmount || feesToUse?.totalFees || 0;
        
        if (!feesToUse || totalFees <= 0) {
          // محاولة حساب الرسوم تلقائياً أولاً
          if (values.amount > 0) {
            console.log('محاولة حساب الرسوم تلقائياً قبل الحفظ...');
            
            try {
              // حساب الرسوم وانتظار اكتمال العملية
              await handleCalculateFees();
              
              // انتظار أطول للتأكد من تحديث الحالة
              await new Promise(resolve => setTimeout(resolve, 500));
              
              // إعادة فحص الرسوم المحدثة من الحالة الحالية
              const updatedTotalFees = calculatedFees?.totalFeesAmount || calculatedFees?.totalFees || 0;
              
              if (!calculatedFees || updatedTotalFees <= 0) {
                setError('لا يمكن حفظ السند. تأكد من أن مجموعة الرسوم تحتوي على رسوم صحيحة أو احسب الرسوم يدوياً أولاً.');
                return;
              }
              
              // تحديث المرجع المحلي للاستخدام في الحفظ
              feesToUse = calculatedFees;
            } catch (error) {
              console.error('خطأ في حساب الرسوم قبل الحفظ:', error);
              setError('فشل في حساب الرسوم. يرجى المحاولة مرة أخرى أو حساب الرسوم يدوياً.');
              return;
            }
          } else {
            setError('لا يمكن حفظ السند برسوم صفر. يجب حساب الرسوم أولاً.');
            return;
          }
        }
      }
      
      setSaveLoading(true);
      setError(null);
      
      try {
        console.log('استخدام الرسوم المحسوبة:', calculatedFees);
        
        // إعداد بيانات السند
        const voucherData = {
          ...values,
          branch: activeBranch._id,
          // تأكد من تضمين نوع السند
          voucherType: 'journal',
          // إضافة الرسوم المحسوبة إذا كانت موجودة
          calculatedFees: feesToUse,
          // إضافة تلميح أن هذا السند من النافذة المصغرة
          fromCompactForm: true
        };
        
        console.log('إرسال بيانات سند الموازنة:', voucherData);
        
        // إرسال البيانات لإنشاء سند جديد باستخدام السياق المناسب
        const { apiWithContext } = await import('../../../services/api');
        await apiWithContext.post('/finance/specialized-vouchers', voucherData, 'specialized-voucher');
        
        setSuccess(true);
        setSaveLoading(false);
        
        // إغلاق النافذة المصغرة بعد 2 ثانية
        setTimeout(() => {
          handleClose();
        }, 2000);
      } catch (error: any) {
        console.error('خطأ في إنشاء سند الموازنة:', error);
        setError(error.response?.data?.message || 'حدث خطأ في إنشاء سند الموازنة');
        setSaveLoading(false);
      }
    }
  });
  
  // عند تغير البيانات، أعد تهيئة النموذج
  useEffect(() => {
    formik.setValues(getInitialValues());
  }, [templateData]);

  // useEffect لحساب الرسوم تلقائياً عند تغيير المبلغ أو مجموعة الرسوم
  useEffect(() => {
    const calculateFeesAutomatically = async () => {
      const { amount, appliedFeeGroupId } = formik.values;
      
      // التحقق من الشروط المطلوبة لحساب الرسوم
      if (templateData?.hasFees && amount > 0 && appliedFeeGroupId) {
        try {
          await handleCalculateFees();
        } catch (error) {
          console.error('خطأ في حساب الرسوم التلقائي:', error);
        }
      }
    };

    // تأخير قصير لتجنب الحسابات المتكررة أثناء الكتابة
    const timeoutId = setTimeout(calculateFeesAutomatically, 300);
    
    return () => clearTimeout(timeoutId);
  }, [formik.values.amount, formik.values.appliedFeeGroupId, templateData?.hasFees]);
  
  // إغلاق النافذة والعودة للصفحة السابقة
  const handleClose = () => {
    setOpen(false);
    navigate(-1);
  };
  
  // الحصول على اسم الحساب من المعرف
  const getAccountName = (accountId: string): string => {
    // جرب البحث بطرق مختلفة
    let account = accounts.find(acc => acc._id === accountId);
    
    // إذا لم يجد، جرب بـ id بدلاً من _id
    if (!account) {
      account = accounts.find(acc => (acc as any).id === accountId);
    }
    
    // إذا لم يجد، جرب تحويل المعرف إلى string
    if (!account) {
      account = accounts.find(acc => String(acc._id) === String(accountId));
    }
    
    return account ? `${account.name} (${account.accountNumber})` : 'حساب غير معروف';
  };
  
  // الحصول على اسم مجموعة الرسوم من المعرف
  const getFeeGroupName = (feeGroupId: string): string => {
    const feeGroup = feeGroups.find(group => group._id === feeGroupId);
    return feeGroup ? feeGroup.name : 'مجموعة رسوم غير معروفة';
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <BalanceIcon sx={{ mr: 1, color: 'secondary.main' }} />
          <Typography variant="h6" component="div">
            {templateData ? templateData.name : 'سند موازنة'}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ py: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
        ) : success ? (
          <Alert severity="success" sx={{ my: 2 }}>تم إنشاء سند الموازنة بنجاح</Alert>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ my: 2 }}>
              <FormControl 
                fullWidth 
                error={formik.touched.debitAccountId && Boolean(formik.errors.debitAccountId)}
                sx={{ mb: 2 }}
              >
                <InputLabel id="debit-account-label">الحساب المدين</InputLabel>
                <Select
                  labelId="debit-account-label"
                  id="debitAccountId"
                  name="debitAccountId"
                  value={formik.values.debitAccountId}
                  onChange={formik.handleChange}
                  label="الحساب المدين"
                  disabled={templateData?.debitAccounts.length === 1}
                >
                  {templateData?.debitAccounts.map(accountId => (
                    <MenuItem key={accountId} value={accountId}>
                      {getAccountName(accountId)}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.debitAccountId && formik.errors.debitAccountId && (
                  <FormHelperText>{formik.errors.debitAccountId}</FormHelperText>
                )}
              </FormControl>
              
              <FormControl 
                fullWidth 
                error={formik.touched.creditAccountId && Boolean(formik.errors.creditAccountId)}
                sx={{ mb: 2 }}
              >
                <InputLabel id="credit-account-label">الحساب الدائن</InputLabel>
                <Select
                  labelId="credit-account-label"
                  id="creditAccountId"
                  name="creditAccountId"
                  value={formik.values.creditAccountId}
                  onChange={formik.handleChange}
                  label="الحساب الدائن"
                  disabled={templateData?.creditAccounts.length === 1}
                >
                  {templateData?.creditAccounts.map(accountId => (
                    <MenuItem key={accountId} value={accountId}>
                      {getAccountName(accountId)}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.creditAccountId && formik.errors.creditAccountId && (
                  <FormHelperText>{formik.errors.creditAccountId}</FormHelperText>
                )}
              </FormControl>
              
              {templateData?.hasFees && templateData.feeGroups.length > 0 && (
                <FormControl
                  fullWidth
                  error={formik.touched.appliedFeeGroupId && Boolean(formik.errors.appliedFeeGroupId)}
                  sx={{ mb: 2 }}
                >
                  <InputLabel id="fee-group-label">مجموعة الرسوم</InputLabel>
                  <Select
                    labelId="fee-group-label"
                    id="appliedFeeGroupId"
                    name="appliedFeeGroupId"
                    value={formik.values.appliedFeeGroupId || ''}
                    onChange={async (e) => {
                      const newFeeGroupId = e.target.value;
                      
                      // تطبيق التغيير في النموذج أولاً
                      formik.handleChange(e);
                      
                      // إعادة تعيين الرسوم المحسوبة
                      setCalculatedFees(null);
                      setError(null);
                      
                      // حساب الرسوم تلقائياً إذا كان المبلغ موجود ومجموعة رسوم جديدة مختارة
                      const currentAmount = formik.values.amount;
                      
                      if (currentAmount > 0 && newFeeGroupId) {
                        // انتظار تحديث formik ثم حساب الرسوم تلقائياً
                        setTimeout(async () => {
                          await handleCalculateFees();
                        }, 100);
                      }
                    }}
                    label="مجموعة الرسوم"
                    disabled={templateData.feeGroups.length === 1}
                  >
                    {templateData.feeGroups.map(feeGroupId => (
                      <MenuItem key={feeGroupId} value={feeGroupId}>
                        {getFeeGroupName(feeGroupId)}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.appliedFeeGroupId && formik.errors.appliedFeeGroupId && (
                    <FormHelperText>{formik.errors.appliedFeeGroupId}</FormHelperText>
                  )}
                </FormControl>
              )}
              
              <TextField
                fullWidth
                id="amount"
                name="amount"
                label="المبلغ"
                type="number"
                value={formik.values.amount}
                onChange={(e) => {
                  // تطبيق التغيير في النموذج
                  formik.handleChange(e);
                  
                  // إعادة تعيين الرسوم المحسوبة عند تغيير المبلغ
                  setCalculatedFees(null);
                  
                  // إزالة أي رسائل خطأ سابقة
                  setError(null);
                }}
                error={formik.touched.amount && Boolean(formik.errors.amount)}
                helperText={
                  formik.touched.amount && formik.errors.amount
                    ? formik.errors.amount
                    : (templateData?.hasFees && templateData.feeGroups.length > 0 && !formik.values.appliedFeeGroupId)
                      ? "يرجى اختيار مجموعة الرسوم أولاً"
                      : undefined
                }
                disabled={templateData?.hasFees && templateData.feeGroups.length > 0 && !formik.values.appliedFeeGroupId}
                InputProps={{
                  startAdornment: <InputAdornment position="start">ر.س</InputAdornment>
                }}
                sx={{ mb: 2 }}
              />
              
              {/* عرض الرسوم المحسوبة إذا كانت موجودة */}
              {calculatedFees && (
                <Box sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  mb: 2,
                  bgcolor: 'background.paper'
                }}>
                  <Typography variant="subtitle2" gutterBottom>
                    تفاصيل الرسوم: {calculatedFees.feeGroupName}
                  </Typography>
                  
                  {calculatedFees.feeItems.map((item) => (
                    <Box key={item.feeId} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {item.feeName}:
                      </Typography>
                      <Typography variant="body2">
                        {isSystemAdmin ? `${item.amount.toFixed(2)} ر.س` : '*** ر.س'}
                      </Typography>
                    </Box>
                  ))}
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" color="error">
                      إجمالي الرسوم:
                    </Typography>
                    <Typography variant="subtitle2" color="error">
                      {isSystemAdmin ? `${calculatedFees.totalFeesAmount.toFixed(2)} ر.س` : '*** ر.س'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="subtitle2" color="primary">
                      المبلغ بعد الرسوم:
                    </Typography>
                    <Typography variant="subtitle2" color="primary">
                      {(formik.values.amount - calculatedFees.totalFeesAmount).toFixed(2)} ر.س
                    </Typography>
                  </Box>
                </Box>
              )}
              
              <TextField
                fullWidth
                id="operationNumber"
                name="operationNumber"
                label="رقم العملية *"
                value={formik.values.operationNumber}
                onChange={formik.handleChange}
                error={formik.touched.operationNumber && Boolean(formik.errors.operationNumber)}
                helperText={formik.touched.operationNumber && formik.errors.operationNumber}
                sx={{ mb: 2 }}
                placeholder="أدخل رقم العملية"
                required
              />
              
              <TextField
                fullWidth
                id="notes"
                name="notes"
                label="ملاحظات"
                multiline
                rows={2}
                value={formik.values.notes}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
              />
            </Box>
          </form>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        borderTop: '1px solid',
        borderColor: 'divider',
        p: 2,
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          color="inherit"
          startIcon={<CloseIcon />}
          disabled={saveLoading}
        >
          إغلاق
        </Button>
        
        {!loading && !error && !success && (
          <Button 
            onClick={() => formik.handleSubmit()}
            variant="contained"
            color="secondary"
            startIcon={saveLoading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saveLoading}
          >
            {saveLoading ? 'جاري الحفظ...' : 'حفظ السند'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CompactBalanceVoucher;